import { create } from 'zustand';
import type { BannerDesign, BannerLayer } from '../model/bannerLayers';
import { emptyBannerDesign } from '../model/bannerLayers';
import { defaultProfileDoc, type ProfileDoc } from '../model/profileDoc';

/**
 * Profile Studio's editing brain — mirrors the community editorStore pattern:
 * document + snapshot history + ephemeral UI in Zustand; server sync stays in
 * hooks. Banner layers edit inside the doc, so one undo stack covers identity
 * tweaks and layer moves alike (drags coalesce into single steps).
 */

const HISTORY_CAP = 100;
const COALESCE_MS = 800;

export type ProfileStudioMode = 'profile' | 'banner';
export type PreviewContextId = 'hero' | 'popup' | 'dm';
export type PreviewWidth = 'desktop' | 'tablet' | 'mobile';

interface ProfileEditorStore {
  doc: ProfileDoc;
  editSeq: number;
  dirty: boolean;
  saving: boolean;
  lastSavedAt: number | null;
  saveError: string | null;
  hasPublished: boolean;

  mode: ProfileStudioMode;
  previewContext: PreviewContextId;
  previewWidth: PreviewWidth;
  selectedLayerId: string | null;
  assetsOpen: boolean;

  past: ProfileDoc[];
  future: ProfileDoc[];
  lastPatchKey: string | null;
  lastPatchAt: number;

  load(doc: ProfileDoc, hasPublished: boolean): void;
  patch(updater: (doc: ProfileDoc) => ProfileDoc, coalesceKey?: string): void;
  patchLayer(id: string, patch: Partial<BannerLayer>, coalesceKey?: string): void;
  setLayers(layers: BannerLayer[], coalesceKey?: string): void;
  design(): BannerDesign;
  undo(): void;
  redo(): void;

  markSaving(): void;
  markSaved(): void;
  markSaveError(msg: string): void;
  setMode(m: ProfileStudioMode): void;
  setPreview(ctx?: PreviewContextId, width?: PreviewWidth): void;
  selectLayer(id: string | null): void;
  setAssetsOpen(open: boolean): void;
}

export const useProfileEditor = create<ProfileEditorStore>((set, get) => ({
  doc: defaultProfileDoc(),
  editSeq: 0,
  dirty: false,
  saving: false,
  lastSavedAt: null,
  saveError: null,
  hasPublished: false,

  mode: 'profile',
  previewContext: 'hero',
  previewWidth: 'desktop',
  selectedLayerId: null,
  assetsOpen: false,

  past: [],
  future: [],
  lastPatchKey: null,
  lastPatchAt: 0,

  load: (doc, hasPublished) =>
    set({
      doc,
      hasPublished,
      editSeq: 0,
      dirty: false,
      saving: false,
      saveError: null,
      past: [],
      future: [],
      lastPatchKey: null,
      selectedLayerId: null,
    }),

  patch: (updater, coalesceKey) => {
    const s = get();
    const now = Date.now();
    const coalesce = coalesceKey != null && coalesceKey === s.lastPatchKey && now - s.lastPatchAt < COALESCE_MS;
    set({
      doc: updater(s.doc),
      past: coalesce ? s.past : [...s.past, s.doc].slice(-HISTORY_CAP),
      future: [],
      editSeq: s.editSeq + 1,
      dirty: true,
      lastPatchKey: coalesceKey ?? null,
      lastPatchAt: now,
    });
  },

  patchLayer: (id, patch, coalesceKey) => {
    get().patch(
      (doc) => ({
        ...doc,
        banner: {
          ...doc.banner,
          design: {
            ...(doc.banner.design ?? emptyBannerDesign()),
            layers: (doc.banner.design?.layers ?? []).map((l) =>
              l.id === id ? ({ ...l, ...patch } as BannerLayer) : l,
            ),
          },
        },
      }),
      coalesceKey,
    );
  },

  setLayers: (layers, coalesceKey) => {
    get().patch(
      (doc) => ({
        ...doc,
        banner: { ...doc.banner, design: { ...(doc.banner.design ?? emptyBannerDesign()), layers } },
      }),
      coalesceKey,
    );
  },

  design: () => get().doc.banner.design ?? emptyBannerDesign(),

  undo: () => {
    const s = get();
    const prev = s.past[s.past.length - 1];
    if (!prev) return;
    set({
      doc: prev,
      past: s.past.slice(0, -1),
      future: [s.doc, ...s.future].slice(0, HISTORY_CAP),
      editSeq: s.editSeq + 1,
      dirty: true,
      lastPatchKey: null,
    });
  },
  redo: () => {
    const s = get();
    const next = s.future[0];
    if (!next) return;
    set({
      doc: next,
      past: [...s.past, s.doc].slice(-HISTORY_CAP),
      future: s.future.slice(1),
      editSeq: s.editSeq + 1,
      dirty: true,
      lastPatchKey: null,
    });
  },

  markSaving: () => set({ saving: true, saveError: null }),
  markSaved: () => set({ saving: false, dirty: false, lastSavedAt: Date.now(), saveError: null }),
  markSaveError: (msg) => set({ saving: false, saveError: msg }),
  setMode: (mode) => set({ mode }),
  setPreview: (ctx, width) =>
    set((s) => ({ previewContext: ctx ?? s.previewContext, previewWidth: width ?? s.previewWidth })),
  selectLayer: (selectedLayerId) => set({ selectedLayerId }),
  setAssetsOpen: (assetsOpen) => set({ assetsOpen }),
}));
